/**
 * VLX Http
 * Lightweight fetch wrapper with events and timeout support
 */

(function () {
    "use strict";

    const VLX = (window.VLX = window.VLX || {});
    const DEFAULT_TIMEOUT = 10000;

    class Http {

        constructor(options = {}) {
            this.options = Object.assign({
                timeout: DEFAULT_TIMEOUT,
                credentials: "same-origin",
                headers: {},
                responseType: "json" // json | text | blob
            }, options);
        }

        async request(url, options = {}) {

            const config = Object.assign({}, this.options, options);

            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), config.timeout);

            const headers = Object.assign({
                "X-Requested-With": "XMLHttpRequest"
            }, config.headers);

            let body = config.body;

            if (body && !(body instanceof FormData)) {
                headers["Content-Type"] = "application/json";
                body = JSON.stringify(body);
            }

            const requestConfig = {
                method: config.method || "GET",
                headers,
                body,
                credentials: config.credentials,
                signal: controller.signal
            };

            if (!this.dispatch("before", { url, config: requestConfig })) {
                clearTimeout(timer);
                return;
            }

            let response;
            let data = null;

            try {
                response = await fetch(url, requestConfig);
            } catch (error) {
                clearTimeout(timer);

                if (error.name === "AbortError") {
                    this.dispatch("timeout", { url });
                    throw new Error("Request timeout");
                }

                this.dispatch("error", { url, error });
                throw error;
            }

            clearTimeout(timer);

            try {
                if (config.responseType === "json") {
                    data = await response.json();
                } else if (config.responseType === "text") {
                    data = await response.text();
                } else if (config.responseType === "blob") {
                    data = await response.blob();
                }
            } catch (e) {
                data = null;
            }

            if (!response.ok) {
                const error = new Error("HTTP Error " + response.status);
                error.response = response;
                error.data = data;

                this.dispatch("error", { url, response, data });
                throw error;
            }

            this.dispatch("success", { url, response, data });

            return data;
        }

        get(url, options = {}) {
            return this.request(url, Object.assign({ method: "GET" }, options));
        }

        post(url, body, options = {}) {
            return this.request(url, Object.assign({
                method: "POST",
                body
            }, options));
        }

        put(url, body, options = {}) {
            return this.request(url, Object.assign({
                method: "PUT",
                body
            }, options));
        }

        patch(url, body, options = {}) {
            return this.request(url, Object.assign({
                method: "PATCH",
                body
            }, options));
        }

        delete(url, options = {}) {
            return this.request(url, Object.assign({ method: "DELETE" }, options));
        }

        dispatch(name, detail = {}) {
            const event = new CustomEvent(`vlx.http.${name}`, {
                cancelable: true,
                detail
            });

            return document.dispatchEvent(event);
        }
    }

    VLX.Http = Http;

})();
